import { useEffect, useRef, useState } from 'react'
import { AuthAPI } from '../../services/auth'
import { setToken } from '../../utils/authStorage'
import { showToast } from '../../utils/showToast'
import { isValidEmail, isValidPassword } from './validation'
import PasswordField from './PasswordField'
import './AuthDialog.css'

type LoginStep = 'first' | 'second' | 'third' | 'fourth'

interface LoginDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function LoginDialog({ open, onClose, onSuccess }: LoginDialogProps) {
  const [step, setStep] = useState<LoginStep>('first')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [newPasswordError, setNewPasswordError] = useState(false)
  const [confirmPasswordError, setConfirmPasswordError] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const timerRef = useRef<number | null>(null)

  const reset = () => {
    setStep('first')
    setEmail('')
    setPassword('')
    setCode('')
    setResetToken('')
    setNewPassword('')
    setConfirmPassword('')
    setEmailError(false)
    setPasswordError(false)
    setNewPasswordError(false)
    setConfirmPasswordError(false)
    setResendSeconds(0)
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    if (!open) reset()
  }, [open])

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
    }
  }, [])

  const startResendTimer = () => {
    setResendSeconds(60)
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          if (timerRef.current) window.clearInterval(timerRef.current)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleLogin = async () => {
    const validEmail = isValidEmail(email)
    const validPassword = password.trim().length > 0
    setEmailError(!validEmail)
    setPasswordError(!validPassword)
    if (!validEmail || !validPassword) return

    try {
      const result = await AuthAPI.emailLogin(email, password.trim())
      setToken(result.token)
      showToast('Login success')
      handleClose()
      setTimeout(() => onSuccess(), 1000)
    } catch {
      showToast('The account password entered is incorrect, please re-enter it')
    }
  }

  const handleFindAccount = () => {
    setStep('second')
  }

  const handleSendResetEmail = async () => {
    const validEmail = isValidEmail(email)
    setEmailError(!validEmail)
    if (!validEmail) return

    try {
      await AuthAPI.sendResetPasswordEmail(email)
      startResendTimer()
      setStep('third')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Network error')
    }
  }

  const handleResend = async () => {
    if (resendSeconds > 0) return
    try {
      await AuthAPI.sendResetPasswordEmail(email)
      startResendTimer()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Network error')
    }
  }

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      showToast('Verification code error, please re-enter')
      return
    }

    try {
      const result = await AuthAPI.verifyResetPasswordCode(email, code.trim())
      setResetToken(result.token)
      setStep('fourth')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Verification code error')
    }
  }

  const handleResetPassword = async () => {
    const validNew = isValidPassword(newPassword)
    const validConfirm = isValidPassword(confirmPassword) && confirmPassword === newPassword
    setNewPasswordError(!validNew)
    setConfirmPasswordError(!validConfirm)
    if (!validNew || !validConfirm) return

    try {
      await AuthAPI.resetPassword(email, resetToken, newPassword)
      showToast('Reset success')
      handleClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Reset failed')
    }
  }

  if (!open) return null

  return (
    <div className="c-dialog-wrapper" role="dialog" aria-modal="true" aria-label="Login">
      <div className="c-dialog dialog-signup dialog-login">
        <div className="c-dialog-header">
          <button type="button" className="close" aria-label="Close" onClick={handleClose} />
        </div>

        {step === 'first' && (
          <div className="c-dialog-content step-first">
            <p className="title">Login</p>
            <div className="form">
              <div className="form-item">
                <span className="label">Email address</span>
                <input
                  className="input-text"
                  type="text"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailError && <p className="help">Format error, please re-enter</p>}
              </div>
              <PasswordField
                value={password}
                onChange={setPassword}
                showError={passwordError}
              />
            </div>
            <p className="help-forget-password">
              Forget password?{' '}
              <button type="button" className="link-btn" onClick={handleFindAccount}>
                Find my account
              </button>
            </p>
            <button type="button" className="btn" onClick={handleLogin}>Login</button>
          </div>
        )}

        {step === 'second' && (
          <div className="c-dialog-content step-second">
            <p className="title">Find my account</p>
            <div className="form">
              <div className="form-item">
                <span className="label">Email address</span>
                <input
                  className="input-text"
                  type="text"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailError && <p className="help">Format error, please re-enter</p>}
              </div>
            </div>
            <button type="button" className="btn" onClick={handleSendResetEmail}>Login</button>
          </div>
        )}

        {step === 'third' && (
          <div className="c-dialog-content step-third">
            <p className="title">Verify email</p>
            <div className="form">
              <div className="form-item">
                <span className="label">Enter verification code</span>
                <input
                  className="input-text"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <p className="help-resent">
                  Did not get verification code?{' '}
                  <button
                    type="button"
                    className="link-btn"
                    disabled={resendSeconds > 0}
                    onClick={handleResend}
                  >
                    {resendSeconds > 0 ? `Resent(${resendSeconds})` : 'Resent'}
                  </button>
                </p>
              </div>
            </div>
            <button type="button" className="btn" onClick={handleVerifyCode}>Next</button>
          </div>
        )}

        {step === 'fourth' && (
          <div className="c-dialog-content step-fourth">
            <p className="title">Reset password</p>
            <div className="form">
              <PasswordField
                value={newPassword}
                onChange={setNewPassword}
                placeholder="8-16 characters, letters + numbers only"
                showError={newPasswordError}
              />
              <div className="form-item">
                <span className="label">Enter confirm password</span>
                <div className="input-text-wrapper">
                  <input
                    className="input-text"
                    type="password"
                    placeholder="8-16 characters, letters + numbers only"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                {confirmPasswordError && (
                  <p className="help">Confirm password error, please re-enter</p>
                )}
              </div>
            </div>
            <button type="button" className="btn" onClick={handleResetPassword}>Login</button>
          </div>
        )}
      </div>
    </div>
  )
}
